import EventTypes from "@wesstron/utils/Api/constants/eventTypes";
import moment from "moment";
import React, {useCallback, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {show} from "redux-modal";
import {showFile} from "../../../actions/filesActions";
import {getFileObject} from "../../../api/files/getFileObject";
import {
    getMostRecentEventIDs,
    getSelectedAnimalForDocuments,
    makeGetAnimalsInGroup,
} from "../../../selectors/animalDocumentsSelectors";
import {
    getFallReasons,
    getSellReasons,
} from "../../../selectors/dictionarySelectors";
import {getEmployeeList, getServiceList} from "../../../selectors/userSelector";
import {formatAnimalName} from "../../../utils/AnimalsUtils";
import {formatLocationName} from "../../../utils/global-formatters/formatLocationName";
import {isMobile} from "../../../utils/MobileUtils";
import {checkIfUserHasPrivilegedAccess} from "../../../utils/NewRolesUtils";
import {employeesValueFormatter} from "../../../utils/RaportsUtils";
import {getAnimalUnit} from "../../../utils/SettingsUtils";
import {convertWeightUnitTo} from "../../../utils/UnitUtils";
import {createKeyToValueDictionary} from "../../../utils/Utils";
import DefaultMobileRow from "../../basics/table-grid/default-mobile-row/DefaultMobileRow";
import TableGrid from "../../basics/table-grid/TableGrid";
import TextSwitch from "../../basics/text-switch/TextSwitch";
import AnimalReferenceDocumentModalGrid from "../../grids/animal-reference-document/AnimalReferenceDocumentModalGrid";
import EditSellOrFallModal, {
    ModalName as EditSellOrFallModalName,
} from "../../modals-new/edit-sell-or-fall-modal/EditSellOrFallModal";
import EditWeightingModal, {
    ModalName as EditWeightingModalName,
} from "../../modals-new/edit-weighting-modal/EditWeightingModal";
import {ModalName as GroupCardRemoveEventModalName} from "../../modals-new/group-card-remove-event-modal/GroupCardRemoveEventModal";
import CollapsableContainer from "../containers/CollapsableContainer";
import {removeRendererHooks} from "../TableComponents";
import WeightingChart from "./WeightingChart";

function EditComponent({value, documentItem}) {
    const dispatch = useDispatch();

    const onClick = useCallback(
        (props) => {
            const group = documentItem.AnmGrp ? documentItem : null;
            const animal = documentItem.AnmID ? documentItem : null;
            if ([EventTypes.FALL, EventTypes.SELL].includes(value.EvCode)) {
                dispatch(
                    show(EditSellOrFallModalName, {event: value, group, animal})
                );
            } else {
                dispatch(
                    show(EditWeightingModalName, {event: value, group, animal})
                );
            }
        },
        [dispatch, value, documentItem]
    );

    if (value.EvCode === EventTypes.INSERTION) return null;
    return <i className="fa-solid fa-edit pointer" onClick={onClick} />;
}

function RemoveComponent({
    value,
    isGroup,
    animalsInGroup,
    documentItem,
    mostRecentEvents,
}) {
    const dispatch = useDispatch();

    const {t} = useTranslation();

    const animal = useMemo(() => {
        return animalsInGroup[value.AnmID];
    }, [value, animalsInGroup]);

    const getRemoveText = useCallback(() => {
        switch (value.EvCode) {
            case EventTypes.FALL:
                return {
                    title: t("removeFall"),
                    text: t("removeFallText", {
                        number: formatAnimalName(animal),
                    }),
                };
            case EventTypes.WEIGHTING:
                return {
                    title: t("removeWeighting"),
                    text: t("removeWeightingTextNoAnimal", {
                        day: moment(value.EvTime).format("L"),
                    }),
                };
            case EventTypes.SELL:
                return {
                    title: t("removeSell"),
                    text: t("removeSellText", {
                        number: formatAnimalName(animal),
                    }),
                };
            default:
                return {title: "?", text: "?"};
        }
    }, [value, animal, t]);

    const onClick = useCallback(() => {
        dispatch(
            show(GroupCardRemoveEventModalName, {
                ...getRemoveText(),
                name: isGroup ? documentItem.GrNo1 : documentItem.AnmNo1,
                AnmID: value.AnmID,
                AnmCnt: value.AnmCnt,
                EvID: value.EvID,
                AnmGrp: isGroup ? documentItem.AnmGrp : undefined,
                isGroup: isGroup,
                useRemoveEvent: value.EvCode === EventTypes.WEIGHTING,
            })
        );
    }, [value, documentItem, dispatch, isGroup, getRemoveText]);

    if (value.EvCode === EventTypes.INSERTION) return null;
    if ([EventTypes.SELL, EventTypes.FALL].includes(value.EvCode)) {
        if (!isGroup) return null;
        return removeRendererHooks({value}, animal, mostRecentEvents, onClick);
    }
    return <i className="fa-solid fa-trash pointer" onClick={onClick} />;
}

function ImagePreview({object}) {
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();

    const file = useMemo(() => object?.Files?.[0], [object]);

    const onClick = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getFileObject(file.FileID, {
                FarmID: object.FarmID,
            });
            dispatch(showFile(res.file));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [dispatch, file?.FileID, object?.FarmID]);

    if (!file) return null;
    if (loading)
        return <i className="fa-solid fa-spin fa-fw fa-circle-notch fa-spin" />;
    return <i className="fa-solid fa-camera pointer" onClick={onClick} />;
}

function WeightingTable({events, isGroup}) {
    const {t} = useTranslation();

    const animalUnit = useRef(getAnimalUnit());
    const userHasPrivilegedAccess = useRef(checkIfUserHasPrivilegedAccess());
    const mobile = useRef(isMobile());

    const getAnimalsInGroup = useMemo(makeGetAnimalsInGroup, []);

    const employees = useSelector(getEmployeeList);
    const service = useSelector(getServiceList);
    const fallReasons = useSelector(getFallReasons);
    const sellReasons = useSelector(getSellReasons);
    const animalsInGroup = useSelector(getAnimalsInGroup);
    const documentItem = useSelector(getSelectedAnimalForDocuments);

    const mostRecentEvents = useSelector((state) =>
        getMostRecentEventIDs(state)([EventTypes.FALL, EventTypes.SELL])
    );

    const animalDictionary = useMemo(
        () => createKeyToValueDictionary(animalsInGroup, "AnmID"),
        [animalsInGroup]
    );

    const dateFormatter = useCallback((value) => moment(value).format("L"), []);

    const weightFormatter = useCallback(
        (value) =>
            convertWeightUnitTo(value, {
                unit: animalUnit.current,
                showUnit: true,
                fixed: 2,
            }),
        []
    );

    const locationFormatter = useCallback(
        (value) => formatLocationName(value),
        []
    );

    const _employeesValueFormatter = useCallback(
        (value) => {
            return employeesValueFormatter(value, employees, service);
        },
        [employees, service]
    );

    const typeFormatter = useCallback(
        (value) => {
            switch (value) {
                case EventTypes.WEIGHTING:
                    return t("weighting");
                case EventTypes.FALL:
                    return t("eventTypes.F");
                case EventTypes.SELL:
                    return t("eventTypes.L");
                case EventTypes.INSERTION:
                    return t("eventTypes.IN");
                default:
                    return value;
            }
        },
        [t]
    );

    const reasonDictionary = useMemo(
        () =>
            createKeyToValueDictionary(
                [...fallReasons.WData, ...sellReasons],
                "ID"
            ),
        [fallReasons, sellReasons]
    );

    const reasonFormatter = useCallback(
        (value) => {
            if (!value) return null;
            const reason = reasonDictionary[value];
            return reason?.Value;
        },
        [reasonDictionary]
    );

    const animalFormatter = useCallback(
        (value) => {
            const animal = animalDictionary[value];
            if (!animal || !(animal.RFID || animal.Tagged)) return null;
            return (
                <AnimalReferenceDocumentModalGrid
                    value={animal.AnmNo1}
                    object={animal}
                />
            );
        },
        [animalDictionary]
    );

    const hasIndividualAnimals = useMemo(() => {
        if (!isGroup) return false;
        return animalsInGroup.some((animal) => {
            return (
                animal.hasOwnProperty("RFID") ||
                (animal.Tagged &&
                    events.some((event) => event.AnmID === animal.AnmID))
            );
        });
    }, [animalsInGroup, events, isGroup]);

    const headers = useMemo(() => {
        const tmp = [
            {
                name: t("tagReaderDrawer.event"),
                field: "EvCode",
                valueFormatter: typeFormatter,
            },
        ];
        if (isGroup) {
            if (hasIndividualAnimals) {
                tmp.push({
                    name: t("animalNumber"),
                    field: "AnmID",
                    valueFormatter: animalFormatter,
                });
            }
            tmp.push({
                name: t("location"),
                field: "EvData.PlcmntID",
                valueFormatter: locationFormatter,
            });
        }
        tmp.push(
            {
                name: t("date"),
                field: "EvTime",
                valueFormatter: dateFormatter,
                disableValueFormatterSort: true,
                _mobileDate: true,
            },
            {
                name: t("amount"),
                field: "AnmCnt",
                _mobileHide: true,
            },
            {
                name: t("avgPieceWeight"),
                field: "EvData.Weight",
                valueFormatter: weightFormatter,
                disableValueFormatterSort: true,
            },
            {
                name: t("reason"),
                field: "EvData.Reasn",
                _mobileHide: true,
                valueFormatter: reasonFormatter,
            },
            {
                name: t("eventGrid.operator"),
                field: "EvData.OperID",
                valueFormatter: _employeesValueFormatter,
                shouldShow: userHasPrivilegedAccess.current,
                colWidth: 2,
            },
            {
                name: t("comment"),
                field: "Comment",
            },
            {
                name: "",
                component: (props) => <ImagePreview {...props} />,
                headerClassName: "index",
                itemClassName: "index",
                notSortable: true,
            },
            {
                name: "",
                component: (props) => (
                    <EditComponent {...props} documentItem={documentItem} />
                ),
                headerClassName: "index",
                itemClassName: "index",
                notSortable: true,
            },
            {
                name: "",
                component: (props) => (
                    <RemoveComponent
                        {...props}
                        isGroup={isGroup}
                        documentItem={documentItem}
                        animalsInGroup={animalDictionary}
                        mostRecentEvents={mostRecentEvents}
                    />
                ),
                headerClassName: "index",
                itemClassName: "index",
                notSortable: true,
            }
        );
        return tmp;
    }, [
        typeFormatter,
        dateFormatter,
        weightFormatter,
        locationFormatter,
        _employeesValueFormatter,
        reasonFormatter,
        animalFormatter,
        hasIndividualAnimals,
        isGroup,
        t,
        documentItem,
        animalDictionary,
        mostRecentEvents,
    ]);

    const data = useMemo(() => {
        return events.slice().sort((a, b) => b.EvTime - a.EvTime);
    }, [events]);

    return (
        <TableGrid
            headers={headers}
            data={data}
            mobileRow={<DefaultMobileRow />}
            showPagination={!mobile.current}
            paginationItems={10}
            scrollOnPageChange={false}
        />
    );
}

export default function WeightingContainer({weightingProps, events, isGroup}) {
    const {t} = useTranslation();

    const [viewMode, setViewMode] = useState("chart");

    const switchOptions = useMemo(
        () => [
            {
                name: (
                    <>
                        <i className="fa-solid fa-chart-scatter me-2" />
                        <span>{t("allReadingsChart")}</span>
                    </>
                ),
                value: "chart",
            },
            {
                name: (
                    <>
                        <i className="fa-solid fa-chart-line me-2" />
                        <span>{t("averageWeightChart")}</span>
                    </>
                ),
                value: "chart-avg",
            },
            {
                name: (
                    <>
                        <i className="fa-solid fa-table me-2" />
                        <span>{t("weightsTable")}</span>
                    </>
                ),
                value: "table",
            },
        ],
        [t]
    );

    return (
        <CollapsableContainer.Card
            id="cy-weighting-card"
            header={t("weighting")}
            defaultExpanded={true}>
            <div>
                <TextSwitch
                    options={switchOptions}
                    value={viewMode}
                    onChange={setViewMode}
                />
            </div>
            {viewMode === "chart-avg" && (
                <WeightingChart {...weightingProps} showAverageWeightOnly />
            )}
            {viewMode === "chart" && <WeightingChart {...weightingProps} />}
            {viewMode === "table" && (
                <WeightingTable events={events} isGroup={isGroup} />
            )}
            <EditWeightingModal />
            <EditSellOrFallModal />
        </CollapsableContainer.Card>
    );
}
